/**
 * CertificateDetailPage.jsx
 * Vista privada de detalle de un certificado del usuario autenticado.
 * Ruta: /home/certificates/:verificationId
 *
 * Layout 2 columnas (60/40): badge + metadata | acciones jerarquizadas
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import apiClient from '../../services/apiClient';
import BadgeVisual from '../../components/programs/BadgeVisual';
import {
  Award, Download, Link2, Check, Linkedin, MessageCircle,
  Instagram, ExternalLink, Loader, AlertCircle, ArrowLeft,
  BookOpen, Copy, Shield
} from 'lucide-react';

export default function CertificateDetailPage() {
  const { verificationId } = useParams();
  const navigate = useNavigate();
  const badgeRef = useRef(null);

  const [badge, setBadge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedId, setCopiedId]   = useState(false);
  const [copiedInsta, setCopiedInsta] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    apiClient.get(`/badges/public/${verificationId}`)
      .then(res => setBadge(res.data.badge))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [verificationId]);

  const publicUrl = `${window.location.origin}/badge/${verificationId}`;
  // ogUrl apunta al endpoint Express que sirve OG meta tags reales para bots (WhatsApp, LinkedIn…)
  // Los humanos que hagan clic reciben redirect 302 a publicUrl (la SPA)
  const ogUrl     = `${window.location.origin}/api/badges/og/${verificationId}`;
  const shortUrl  = `/badge/${verificationId.slice(0, 8)}…${verificationId.slice(-4)}`;

  async function handleDownload() {
    if (!badgeRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(badgeRef.current, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `insignia-epl-${verificationId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error al generar PNG:', err);
    } finally {
      setDownloading(false);
    }
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    });
  }

  function handleCopyId() {
    navigator.clipboard.writeText(verificationId).then(() => {
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2500);
    });
  }

  function handleLinkedIn() {
    // ogUrl → Express devuelve OG HTML para el scraper de LinkedIn
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(ogUrl)}`, '_blank', 'noopener,noreferrer');
  }

  function handleWhatsApp() {
    // ogUrl → WhatsApp scraper recibe OG tags reales; humanos son redirigidos a publicUrl
    const text = `Acabo de obtener la ${badge?.badgeLabelSnapshot} en EduTech Pulse Lab. Mira mi insignia: ${ogUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  }

  function handleInstagram() {
    const text = `Acabo de obtener la ${badge?.badgeLabelSnapshot} en EduTech Pulse Lab.\n🔗 Verifica en: ${publicUrl}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedInsta(true);
      setTimeout(() => setCopiedInsta(false), 2500);
    });
  }

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-16 text-slate-500">
        <Loader className="w-5 h-5 animate-spin text-cyan-500" />
        <span className="text-sm">Cargando certificado...</span>
      </div>
    );
  }

  if (notFound || !badge) {
    return (
      <div className="max-w-sm mx-auto px-4 py-16 text-center">
        <AlertCircle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Certificado no encontrado</h2>
        <p className="text-sm text-slate-600 mb-4">No pudimos encontrar este certificado en tu cuenta.</p>
        <button onClick={() => navigate('/home/certificates')} className="text-sm text-cyan-600 hover:underline">
          Volver a mis certificados
        </button>
      </div>
    );
  }

  const dateStr = badge.claimedAt
    ? new Date(badge.claimedAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 py-8">

      {/* ── Breadcrumb ── */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate('/home/certificates')}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Mis certificados
        </button>
        {badge.programSlug && (
          <a
            href={`/p/${badge.programSlug}`}
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-700 transition-colors"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Volver al programa
          </a>
        )}
      </div>

      {/* ── Header ── */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full mb-2">
          <Award className="w-3.5 h-3.5" />
          Insignia emitida
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 leading-snug">
          {badge.badgeLabelSnapshot}
        </h1>
        <p className="text-sm text-slate-400 mt-2">{badge.programTitleSnapshot}</p>
      </div>

      {/* ── Grid 2 columnas ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

        {/* ══ COLUMNA IZQUIERDA (60%) ══ */}
        <div className="lg:col-span-3 space-y-6">

          {/* Badge visual */}
          <div className="flex justify-center lg:justify-start">
            <div className="w-full">
              <BadgeVisual ref={badgeRef} badge={badge} />
            </div>
          </div>

          {/* Ficha de metadata verificable */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-3.5 h-3.5 text-slate-300" />
              <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Datos de verificación</h2>
            </div>
            <dl className="space-y-3">
              {badge.displayNameSnapshot && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-4">
                  <dt className="text-xs font-semibold text-slate-400 sm:w-28 flex-shrink-0">Titular</dt>
                  <dd className="text-sm font-semibold text-slate-900">{badge.displayNameSnapshot}</dd>
                </div>
              )}
              {dateStr && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-4">
                  <dt className="text-xs font-semibold text-slate-400 sm:w-28 flex-shrink-0">Emitida</dt>
                  <dd className="text-sm text-slate-700">{dateStr}</dd>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-4">
                <dt className="text-xs font-semibold text-slate-400 sm:w-28 flex-shrink-0">Estado</dt>
                <dd className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full w-fit">Verificada</dd>
              </div>
              {badge.programTitleSnapshot && (
                <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-4">
                  <dt className="text-xs font-semibold text-slate-400 sm:w-28 flex-shrink-0">Programa</dt>
                  <dd className="text-sm text-slate-700">{badge.programTitleSnapshot}</dd>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-4 pt-3 border-t border-slate-100">
                <dt className="text-xs font-semibold text-slate-400 sm:w-28 flex-shrink-0 mt-0.5">ID verificación</dt>
                <dd className="flex items-start gap-2 min-w-0">
                  <code className="text-[11px] text-slate-500 font-mono leading-relaxed break-all">{verificationId}</code>
                  <button
                    onClick={handleCopyId}
                    className="flex-shrink-0 mt-0.5 text-slate-300 hover:text-cyan-600 transition-colors"
                    title="Copiar ID"
                  >
                    {copiedId ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* ══ COLUMNA DERECHA (40%) ══ */}
        <div className="lg:col-span-2 space-y-3">

          {/* Panel: Acciones */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2.5">Acciones</p>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg text-sm transition-colors disabled:opacity-60"
            >
              <Download className="w-4 h-4" />
              {downloading ? 'Generando…' : 'Descargar insignia (PNG)'}
            </button>
          </div>

          {/* Panel: Verificación */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2.5">Verificación</p>
            <div className="space-y-2">
              <button
                onClick={handleCopyLink}
                className="w-full inline-flex items-center gap-2 px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium rounded-lg text-sm transition-colors"
              >
                {copiedLink ? <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /> : <Link2 className="w-4 h-4 flex-shrink-0 text-slate-400" />}
                <span className="flex-1 text-left">{copiedLink ? '¡Enlace copiado!' : 'Copiar enlace verificable'}</span>
              </button>
              <a
                href={`/badge/${verificationId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center gap-2 px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium rounded-lg text-sm transition-colors"
              >
                <ExternalLink className="w-4 h-4 flex-shrink-0 text-slate-400" />
                <span className="flex-1 text-left">Ver página pública verificable</span>
              </a>
            </div>
          </div>

          {/* Panel: Compartir */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2.5">Compartir</p>
            <div className="space-y-2">
              <button
                onClick={handleLinkedIn}
                className="w-full inline-flex items-center gap-2 px-4 py-2 border border-slate-200 hover:bg-blue-50 hover:border-blue-200 text-slate-700 hover:text-blue-700 font-medium rounded-lg text-sm transition-colors"
              >
                <Linkedin className="w-4 h-4 flex-shrink-0 text-[#0a66c2]" />
                <span className="flex-1 text-left">LinkedIn</span>
              </button>
              <button
                onClick={handleWhatsApp}
                className="w-full inline-flex items-center gap-2 px-4 py-2 border border-slate-200 hover:bg-green-50 hover:border-green-200 text-slate-700 hover:text-green-700 font-medium rounded-lg text-sm transition-colors"
              >
                <MessageCircle className="w-4 h-4 flex-shrink-0 text-[#25D366]" />
                <span className="flex-1 text-left">WhatsApp</span>
              </button>
              <button
                onClick={handleInstagram}
                className="w-full inline-flex items-center gap-2 px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium rounded-lg text-sm transition-colors"
              >
                {copiedInsta
                  ? <Check className="w-4 h-4 flex-shrink-0 text-emerald-500" />
                  : <Instagram className="w-4 h-4 flex-shrink-0 text-slate-400" />
                }
                <span className="flex-1 text-left text-xs">
                  {copiedInsta ? '¡Texto copiado!' : 'Copiar texto sugerido para Instagram'}
                </span>
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
